<script lang="ts">
/**
 * Drill-down breadcrumb for the containers index page.
 *
 * Shows the ancestor path of the current drill-down scope. Each segment
 * navigates to that level by visiting `/containers?parent_id=<id>`.
 * The root segment ("Containers") links to `/containers` with no param.
 *
 * @example
 * ```vue
 * <ContainerBreadcrumb :segments="breadcrumb" />
 * ```
 */
export interface ContainerBreadcrumbProps {
  /** Ordered breadcrumb segments from root ancestor to current container. */
  segments: BreadcrumbSegment[]
}
</script>

<script setup lang="ts">
import { computed } from "vue"

import { containersPath } from "@/routes"
import type { BreadcrumbSegment } from "@/types/models"

const props = defineProps<ContainerBreadcrumbProps>()

const items = computed(() => {
  const result: { label: string; to?: string }[] = [{ label: "Containers", to: containersPath() }]

  props.segments.forEach((segment, index) => {
    const isLast = index === props.segments.length - 1
    result.push({
      label: segment.name,
      to: isLast ? undefined : `${containersPath()}?parent_id=${segment.id}`,
    })
  })

  return result
})
</script>

<template>
  <UBreadcrumb :items="items" />
</template>
